import { Bell } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Activite, GenreActivite } from '../../firebase/activite';
import { listActivite, marquerActiviteVue, watchActiviteNeuves } from '../../firebase/activite';
import { Avatar } from '../../components/ui/Avatar';
import { Sheet } from '../../components/ui/Sheet';
import { useSession } from '../../store/SessionContext';
import { compte, conversationTime } from '../../utils/format';
import { t } from '../../i18n';
import { commentairesPath } from '../../utils/routes';

/**
 * La cloche du fil.
 *
 * Ce qui arrive a ce qu'on publie n'avait aucun endroit ou se lire. Une
 * notification passe et s'oublie ; si le telephone etait eteint, ou le
 * reglage coupe, personne n'apprenait jamais qu'un post avait plu.
 *
 * Le registre est tenu par les fonctions, **meme quand la notification ne
 * part pas** — c'est ce qui fait sa valeur : il dit ce qui s'est passe
 * pendant qu'on n'etait pas la.
 *
 * La pastille rouge se decide sur une seule comparaison : la date de la
 * derniere ligne contre celle de la derniere ouverture. Un drapeau par
 * ligne demanderait autant d'ecritures qu'il y a de lignes ; une date en
 * demande une.
 */

/*
  Le verbe suffit a dire ce qui s'est passe.

  Une icone la reprenait en bout de ligne — un coeur derriere « a aime »,
  une arobase derriere « t'a cite ». Elle ne disait rien de plus que la
  phrase et attirait l'oeil au mauvais endroit : a droite, la ou il n'y a
  rien a lire.
*/
const VERBES: Record<GenreActivite, string> = {
  like: t.fil.cloche.verbes.like,
  commentaire: t.fil.cloche.verbes.commentaire,
  mention: t.fil.cloche.verbes.mention,
  signet: t.fil.cloche.verbes.signet,
};

export function Cloche() {
  const { user } = useSession();
  const navigate = useNavigate();
  const bouton = useRef<HTMLButtonElement>(null);

  const [ouverte, setOuverte] = useState(false);
  const [neuves, setNeuves] = useState(0);
  const [lignes, setLignes] = useState<Activite[] | null>(null);

  useEffect(() => {
    if (!user) return;
    return watchActiviteNeuves(setNeuves);
  }, [user]);

  function ouvrir() {
    setOuverte(true);
    setLignes(null);
    void listActivite()
      .then(setLignes)
      .catch(() => setLignes([]));
    /*
      Vue des l'ouverture, pas a la fermeture : on a sous les yeux ce qui
      etait neuf, et la pastille n'a plus rien a annoncer. L'attendre
      aurait laisse la pastille allumee pendant qu'on lit.
    */
    void marquerActiviteVue()
      .then(() => setNeuves(0))
      .catch(() => undefined);
  }

  return (
    <>
      <button
        ref={bouton}
        type="button"
        className="feed__cloche"
        aria-label={neuves ? t.fil.cloche.nouvelles(neuves) : t.fil.cloche.titre}
        onClick={ouvrir}
      >
        <Bell size={19} weight="bold" />
        {/* Le nombre dans la pastille : ce qui attend derriere l'icone. */}
        {neuves > 0 && <span className="pastille pastille--compte">{compte(neuves)}</span>}
      </button>

      <Sheet open={ouverte} anchor={bouton} large onClose={() => setOuverte(false)}>
        <p className="cloche__titre">{t.fil.cloche.activite}</p>

        {lignes === null && (
          <div className="cloche__creux">
            <p className="cloche__vide">{t.commun.unInstant}</p>
          </div>
        )}

        {lignes?.length === 0 && (
          <div className="cloche__creux">
            <p className="cloche__vide">{t.fil.cloche.vide}</p>
          </div>
        )}

        {/* La liste defile dans la fenetre : elle ne la fait pas grandir. */}
        {lignes && lignes.length > 0 && (
          <ul className="cloche__liste">
            {lignes.map((ligne) => {
              const verbe = VERBES[ligne.genre];
              return (
                <li key={ligne.id}>
                  <button
                    type="button"
                    className={`cloche__ligne${ligne.neuve ? ' cloche__ligne--neuve' : ''}`}
                    onClick={() => {
                      setOuverte(false);
                      if (ligne.postId) navigate(commentairesPath(ligne.postId, ligne.commentId));
                    }}
                  >
                    {ligne.auteur && <Avatar user={ligne.auteur} size={36} />}

                    <span className="cloche__corps">
                      <span className="cloche__texte">
                        <strong>{ligne.nom}</strong> {verbe}
                      </span>
                      <span className="cloche__quand">{conversationTime(new Date(ligne.quand).toISOString())}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Sheet>
    </>
  );
}
